'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

/**
 * Custom hook for managing drawer state via URL parameters
 *
 * @param drawerName - The name of the drawer to manage (used as URL parameter value)
 * @returns Object containing isOpen state and setOpen function
 */
export function useDrawerState(drawerName: string) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Check if this drawer is open based on URL parameter
  const isOpen = searchParams.get('drawer') === drawerName;

  // Function to update the URL when drawer state changes
  const setOpen = useCallback(
    (open: boolean) => {
      // Create a new URLSearchParams object to avoid modifying the original
      const params = new URLSearchParams(searchParams.toString());

      if (open) {
        params.set('drawer', drawerName);
      } else {
        params.delete('drawer');
      }

      // Preserve other query parameters if they exist
      const newUrl = params.toString() ? `?${params.toString()}` : '';

      // Use replace instead of push to avoid cluttering browser history
      router.replace(newUrl, { scroll: false });
    },
    [router, searchParams, drawerName]
  );

  return { isOpen, setOpen };
}
